import {differenceInCalendarDays} from 'date-fns';
import {toCalendarDate} from '../helpers/date';
import type {TAppFunction} from '../services/i18n';
import type {ApprovalRequestIndexResponse, ApprovalRequestShowResponse} from '../generated-api';

/**
 * The backend calls the whole agenda "approval requests". A record is either a
 * completed external course (`course_completion`, "absolvování"), a request for
 * a specific course (`request_course`) or one that only names a topic and waits
 * for a manager to source a course for it (`request_topic`).
 */
export const APPROVAL_REQUEST_TYPES = ['course_completion', 'request_course', 'request_topic'] as const;

export type ApprovalRequestType = typeof APPROVAL_REQUEST_TYPES[number];

/** `draft` is what the UI calls "čeká na schválení" -- there is no earlier state. */
export const APPROVAL_REQUEST_STATUSES = ['draft', 'approved', 'rejected', 'cancelled'] as const;

export type ApprovalRequestStatus = typeof APPROVAL_REQUEST_STATUSES[number];

export const APPROVAL_PENDING_STATUS: ApprovalRequestStatus = 'draft';

/** Only learning requests are decided; a course completion is approved on creation. */
export const APPROVAL_DECIDABLE_TYPES: ApprovalRequestType[] = ['request_course', 'request_topic'];

/** The two modes of the student's request form; a completion is a manager's record. */
export const LEARNING_REQUEST_TYPES: ApprovalRequestType[] = APPROVAL_DECIDABLE_TYPES;

/**
 * Levels the rest of the app offers (`components.competenceDialog` in the
 * AngularJS shell). `ApprovalRequestCompetence` would take 0..4, but nothing
 * else in the product exposes those, so the request form stays consistent.
 */
export const COMPETENCE_LEVELS = [1, 2, 3] as const;

export interface ApprovalRequestInfo extends ApprovalRequestIndexResponse {
    /** virtual column: display value and the server-side sort key for the user */
    user_name?: string;
    /**
     * Virtual column carrying the server-side sort key. Expiration itself rides on
     * the certificate association and only the per-user endpoint serializes it --
     * the management list leaves it undefined.
     */
    subject_expiration_date?: Date | null;
}

export interface ApprovalRequestDetail extends ApprovalRequestShowResponse {}

export const toApprovalRequestInfo = (item: ApprovalRequestIndexResponse): ApprovalRequestInfo => ({
    ...item,
    user_name: item.user?.full_name,
    subject_expiration_date: item.external_certificate?.expiration_date,
});

/** A request for training, as opposed to a completed course being recorded. */
export function isLearningRequest(item: {request_type: string}): boolean {
    return APPROVAL_DECIDABLE_TYPES.includes(item.request_type as ApprovalRequestType);
}

/**
 * The justification as the requester wrote it, or nothing.
 *
 * `LearningRequest` validates the presence of one, so `Create#build_requestable`
 * falls back to the topic and then to the course title when a request is filed
 * without it -- which a manager may do, the field being optional on their side.
 * Echoing the title back under "Odůvodnění" only reads as a filled-in field, so
 * the filler counts as no justification at all.
 *
 * `Create` stores the same words on the record's own `reason`, which is what
 * tells the two apart: no reason, no justification -- however the title reads by
 * now, and however short a real justification happens to be.
 */
export function approvalJustification(item: {
    learning_request?: {justification?: string} | null,
    reason?: string | null,
    subject_title?: string | null,
    subject_content?: string | null,
}): string | undefined {
    const value = item.learning_request?.justification;
    if (!value || !item.reason) {
        return undefined;
    }
    if (item.reason === value) {
        return value;
    }
    // `Reject` overwrites reason with its own and leaves the justification as
    // filed, so there the filler can only be spotted by what it repeats
    return (value === item.subject_content || value === item.subject_title) ? undefined : value;
}

/** A pending record still waiting for someone to approve or reject it. */
export function isApprovalPending(item: {status: string, request_type: string}): boolean {
    return item.status === APPROVAL_PENDING_STATUS && isLearningRequest(item);
}

/**
 * Whether approve/bulk_approve may be offered. `realization_complete` mirrors
 * the checklist Approve enforces server side (title, date, provider, price and
 * a leveled competence), so a thematic request without a sourced course is out.
 */
export function isApprovable(item: {status: string, request_type: string, realization_complete?: boolean | null}): boolean {
    return isApprovalPending(item) && item.realization_complete === true;
}

/**
 * `ApprovalRequest::REALIZATION_REQUIRED_ATTRIBUTES` plus a levelled competence
 * -- the same checklist `Approve` enforces and `realization_complete` reports,
 * resolved down to the individual labels so the UI can name what is still
 * missing instead of listing all five every time.
 *
 * The four attributes ride on every serialization; the competences only on the
 * detail (`competences` is outside the list's includes). Where they are absent
 * the missing competence is deduced instead: the checklist has exactly these
 * five items, so a record whose four attributes are filled while the server
 * still reports `realization_complete: false` can only be lacking that one.
 *
 * Order follows the form, so the hint reads in the order the fields are asked for.
 */
export function approvalMissingFields(item: {
    subject_title?: string | null,
    subject_provider?: string | null,
    price?: number | null,
    subject_date?: Date | string | null,
    realization_complete?: boolean | null,
    competences?: { competence_level?: number | null }[],
}): string[] {
    const missing: string[] = [];
    if (!item.subject_title?.trim()) {
        missing.push('approvals.field.courseName');
    }
    if (!item.subject_provider?.trim()) {
        missing.push('approvals.field.provider');
    }
    // a price of zero is a filled-in price, the way `present?` reads it server side
    if (item.price === null || item.price === undefined) {
        missing.push('approvals.field.pricePerStudent');
    }
    if (!item.subject_date) {
        missing.push('approvals.field.term');
    }
    if (item.competences) {
        if (!item.competences.length || item.competences.some(c => !c.competence_level)) {
            missing.push('approvals.field.competencesWithLevel');
        }
    } else if (item.realization_complete === false && !missing.length) {
        missing.push('approvals.field.competencesWithLevel');
    }
    return missing;
}

/**
 * Why a decision was refused, in words the manager can act on.
 *
 * On a single record a 403 comes from `ApprovalRequestPolicy#approve?`, whose
 * scope is the *active* members of the space -- so the requester has left it, in
 * practice by being archived. The body says nothing usable in that case (Pundit's
 * exception serializes as a bare string), which is why the text is ours.
 *
 * `bulk_approve` is authorized differently -- a controller-level check on the
 * acting user's own management access, before any record is looked at -- so
 * naming the requester there would point at the wrong person. A 400 is a
 * business refusal from `Approve`/`Reject` and does carry a localized message.
 */
export function approvalDecisionError(error: { code?: string, message?: string } | undefined,
    t: TAppFunction, isBulk?: boolean): string {
    if (String(error?.code) === '403') {
        return t(isBulk ? 'approvals.error.notAuthorized' : 'approvals.error.notManageable');
    }
    return error?.message || t('approvals.error.decisionFailed');
}

/**
 * Whether the edit screen may open this record.
 *
 * `ApprovalFormPage` routes the two halves of the agenda to two forms: a
 * completion is edited as filed, a learning request is worked up into something
 * decidable (`ApprovalRequestEditForm`). `Update` refuses a decided learning
 * request, so only a pending one may be opened; a completion has no such state.
 */
export function isApprovalEditable(item: {status: string, request_type: string}): boolean {
    return !isLearningRequest(item) || item.status === APPROVAL_PENDING_STATUS;
}

/**
 * Whether the requester may still withdraw it. `Cancel` refuses anything that is
 * not a pending learning request; ownership is the caller's business, since the
 * cancel endpoint lives under the user whose records are being listed.
 */
export function isApprovalCancellable(item: {status: string, request_type: string}): boolean {
    return isApprovalPending(item);
}

/**
 * A thematic request never carries a title until a manager sources a course for
 * it -- what the requester submitted is the topic in `subject_content`.
 */
export function approvalSubjectLabel(item: {subject_title?: string | null, subject_content?: string | null}): string {
    return item.subject_title || item.subject_content || '';
}

export enum ApprovalValidityStatus {
    VALID = 'valid',
    EXPIRING = 'expiring',
    EXPIRED = 'expired',
}

/**
 * A record within this many days of its expiration date is flagged as expiring.
 * Mirrored by EXPIRING_SOON_DAYS in
 * `client/src/app/main/userManagement/userDetailDialog/userDetailDialog.controller.js`.
 */
export const EXPIRING_SOON_DAYS = 14;

export interface ApprovalValidity {
    status: ApprovalValidityStatus;
    date: Date;
    days: number;
}

/**
 * Validity is derived on the client from the expiration date alone -- the API
 * carries no validity state.
 */
export function getApprovalValidity(expiration?: Date | string | null): ApprovalValidity | undefined {
    // a date-only value, so it has to mean the same calendar day in every timezone
    const date = toCalendarDate(expiration);
    if (!date) {
        return undefined;
    }
    const days = differenceInCalendarDays(date, new Date());
    let status;
    if (days < 0) {
        status = ApprovalValidityStatus.EXPIRED;
    } else if (days <= EXPIRING_SOON_DAYS) {
        status = ApprovalValidityStatus.EXPIRING;
    } else {
        status = ApprovalValidityStatus.VALID;
    }
    return {status, date, days};
}
